/**
 * ENTITIES HOOK - API Methods & React Query Hooks Summary
 *
 * ===========================================
 * API METHODS (Internal Functions):
 * ===========================================
 * • getEntities() - GET /entities - Fetch entities with filters (organisation_ids, etc.)
 *
 * ===========================================
 * REACT QUERY HOOKS (Exported):
 * ===========================================
 * • useEntities(organisationIds, enabled?) - Query: Get entities by organisation IDs
 *
 * ===========================================
 * KEY FEATURES:
 * ===========================================
 * • Authentication via Bearer token
 * • Fetches from EOR API
 * • 5-minute stale time
 * • Supports filtering by organisation_ids
 * ===========================================
 */

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { buildQueryParams, DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from '@workspace/ui/lib';
import type { ApiResponse } from '@workspace/ui/types';
import fetchAPI from '@/lib/fetch-api';
import type { Entity, GetEntitiesRequest } from '@/types/entities';

/**
 * Fetches entities from EOR API
 * @param {GetEntitiesRequest} data - Query parameters including organisation_ids
 * @throws {Error} If the API request fails
 * @returns {Promise} The entities response with data and pagination
 */
const getEntities = async (
  data?: GetEntitiesRequest
): Promise<{
  message: string;
  data: Entity[];
  pagination: ApiResponse['pagination'] | null;
}> => {
  const apiUrl = process.env.NEXT_PUBLIC_EOR_API_URL;

  if (!apiUrl) {
    throw new Error('NEXT_PUBLIC_EOR_API_URL environment variable is not set');
  }

  const queryParams = buildQueryParams({
    current_page: data?.current_page ?? DEFAULT_PAGE_INDEX,
    per_page: data?.per_page ?? DEFAULT_PAGE_SIZE,
    organisation_ids: data?.organisation_ids,
    country_ids: data?.country_ids,
    statuses: data?.statuses,
    search: data?.search,
    date: data?.date,
  });

  const response = await fetchAPI<Entity[]>(`${apiUrl}/entities?${queryParams.toString()}`, {
    method: 'GET',
  });

  if (!response.success) {
    throw new Error(response.error || response.message || 'Failed to fetch entities');
  }

  return {
    message: response.message || 'Entities fetched successfully',
    data: response.data,
    pagination: response.pagination || null,
  };
};

/**
 * React Query hook to fetch entities by organisation IDs
 * @param {number[] | undefined} organisationIds - Array of organisation IDs to filter entities
 * @param {boolean} enabled - Whether the query should be enabled
 * @returns {UseQueryResult} Query result containing entities data
 */
export function useEntities(
  organisationIds: number[] | undefined,
  enabled: boolean = true
): UseQueryResult<
  {
    message: string;
    data: Entity[];
    pagination: ApiResponse['pagination'] | null;
  },
  Error
> {
  return useQuery({
    queryKey: ['entities', organisationIds],
    queryFn: () => getEntities({ organisation_ids: organisationIds }),
    enabled: enabled && !!organisationIds && organisationIds.length > 0,
    staleTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
}
